import { Alert, Button, Stack } from '@mui/material'
import type {
  PostTaskSchedulesRequestBody,
  TaskScheduleFieldError,
} from '@ulysses-inc/harami_api_client'
import { useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { HEADER_HEIGHT, Header } from 'src/components/header/Header'
import { HeaderBackButtonMui } from 'src/components/header/HeaderBackButtonMui'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import Loading from 'src/components/loading/Loading'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { isHTTPError, useCreateTaskSchedule } from './api/useCreateTaskSchedule'
import { useQueryTaskSchedulePlaceGroups } from './api/useQueryTaskSchedulePlaceGroups'
import { useQueryTaskScheduleSimulation } from './api/useQueryTaskScheduleSimulation'
import { useQueryTaskScheduleTemplates } from './api/useQueryTaskScheduleTemplates'
import { BasicsSection } from './form/BasicsSection'
import { RecurrenceSection } from './form/RecurrenceSection'
import {
  type TaskScheduleForm,
  clientErrorsOf,
  defaultTaskScheduleForm,
  hasRequiredBasics,
  toRequestBody,
} from './form/taskScheduleForm'
import { SimulationPanel } from './simulation/SimulationPanel'

/**
 * 作業指示の作成（仮UI・案E）。HRMSHI-136。
 *
 * pos-E の表示を画面越しに確かめるための仮の画面で、本実装（HRMSHI-132）には昇格させない。
 * 判定の線引き: 欄の値だけで決まるもの（必須・範囲・2 欄の大小）はフォームで見る（clientErrorsOf）。
 * 欄同士の関係やドメインの規則の正はサーバにあり、保存の 400 が拾って欄に出す。
 * 開始日の計算はフロントに置かず、「試算する」で試算 API（POST /taskSchedules/simulate）の結果を描く。
 * 一覧・編集・削除は無い。作成して記録アプリの「やること」に出ることが確かめられれば足りる。
 */
export const CreateTaskSchedulePage = () => {
  const history = useHistory()
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar()
  const methods = useForm<TaskScheduleForm>({
    defaultValues: defaultTaskScheduleForm(new Date()),
    mode: 'onChange',
  })
  // watch() は React Compiler と相性が悪いので、安定参照を返す useWatch で全欄を読む
  const form = useWatch({ control: methods.control }) as TaskScheduleForm
  const body = toRequestBody(form)
  const basicsReady = hasRequiredBasics(form)
  const clientErrors = clientErrorsOf(form, new Date())
  const hasClientErrors = Object.keys(clientErrors).length > 0

  const templatesQuery = useQueryTaskScheduleTemplates()
  const placeGroupsQuery = useQueryTaskSchedulePlaceGroups()

  // 試算は「試算する」を押した時点の body で呼ぶ。入力のたびには呼ばない
  const [simulatedBody, setSimulatedBody] =
    useState<PostTaskSchedulesRequestBody | null>(null)
  const simulationQuery = useQueryTaskScheduleSimulation(simulatedBody)
  const isSimulationStale =
    simulatedBody !== null &&
    JSON.stringify(simulatedBody) !== JSON.stringify(body)

  const { mutate, isPending } = useCreateTaskSchedule()

  // 保存で 400 が返ったときの欄ごとの理由（fieldErrors）。次に保存を押すまで欄の下に出しておく
  const [saveFieldErrors, setSaveFieldErrors] = useState<
    TaskScheduleFieldError[]
  >([])
  // 400 に fieldErrors が無いとき（body の形そのものが読めないとき）だけ、errors をそのまま出す
  const [saveErrors, setSaveErrors] = useState<string[]>([])

  // 欄に出す理由。field は API の欄名（name / recurrenceRule.startTime など）。
  // 保存の 400 が正で、試算が返した理由は入力が変わって古くなったら出さない
  const errorsOf = (field: string): string[] => {
    const simulationErrors =
      !isSimulationStale && simulationQuery.data?.accepted === false
        ? simulationQuery.data.errors
        : []
    return [...saveFieldErrors, ...simulationErrors]
      .filter(e => e.field === field)
      .map(e => e.message)
  }

  const canSimulate = basicsReady && !hasClientErrors
  const canSave = basicsReady && !hasClientErrors && !isPending

  const onSave = () => {
    setSaveFieldErrors([])
    setSaveErrors([])
    mutate(body, {
      onSuccess: created => {
        showSuccessSnackbar(`作業指示を保存しました（ID: ${created.id}）`)
        methods.reset(defaultTaskScheduleForm(new Date()))
        setSimulatedBody(null)
      },
      onError: error => {
        if (isHTTPError(error)) {
          if (error.fieldErrors && error.fieldErrors.length > 0) {
            setSaveFieldErrors(error.fieldErrors)
          } else {
            setSaveErrors(error.errors)
          }
        }
        showErrorSnackbar('作業指示を保存できませんでした')
      },
    })
  }

  const isLoadingMasters =
    templatesQuery.isLoading || placeGroupsQuery.isLoading

  return (
    <FormProvider {...methods}>
      <Stack>
        <Header
          sticky
          rightSlot={[
            <Button
              key="save"
              id="task-schedule-save"
              variant="contained"
              disabled={!canSave}
              onClick={onSave}
            >
              保存
            </Button>,
          ]}
        >
          <HeaderBackButtonMui onClick={() => history.push('/schedules')} />
          <HeaderTitle title="作業指示の作成" isBeta />
        </Header>

        {isLoadingMasters ? (
          <Loading />
        ) : (
          // 旧スケジュールの編集（EditScheduleContent）と同じ余白と幅。右に試算のペインを足したぶんだけ広い
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={3}
            alignItems="flex-start"
            py={4}
            px={3}
            sx={{ maxWidth: 900 + 24 + 380, width: '100%', mx: 'auto' }}
          >
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              {saveErrors.length > 0 && (
                <Alert severity="error" onClose={() => setSaveErrors([])}>
                  <Stack spacing={0.5}>
                    {saveErrors.map(message => (
                      <span key={message}>{message}</span>
                    ))}
                  </Stack>
                </Alert>
              )}
              <BasicsSection
                templates={templatesQuery.data ?? []}
                placeNodes={placeGroupsQuery.data ?? []}
                errorsOf={errorsOf}
              />
              <RecurrenceSection
                errorsOf={errorsOf}
                clientErrors={clientErrors}
              />
            </Stack>
            {/* 右ペインはスクロールに追随する。sticky は行（左右を並べる Stack）の高さの中で動くので、列自身に付ける */}
            <Stack
              sx={{
                width: { xs: '100%', lg: 380 },
                flexShrink: 0,
                position: { lg: 'sticky' },
                top: { lg: HEADER_HEIGHT + 32 },
                alignSelf: 'flex-start',
              }}
            >
              <SimulationPanel
                form={form}
                clientErrors={clientErrors}
                simulation={simulationQuery.data}
                isStale={isSimulationStale}
                canSimulate={canSimulate}
                isFetching={simulationQuery.isFetching}
                isError={simulationQuery.isError}
                onSimulate={() => setSimulatedBody(body)}
              />
            </Stack>
          </Stack>
        )}
      </Stack>
    </FormProvider>
  )
}
